import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { upgradeImageUrl, FALLBACK_HERO, FALLBACK_THUMB } from "@/lib/imageUtils";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
    Zap,
    Calendar,
    Eye,
    ArrowLeft,
    Share2,
    MessageCircle,
    Trophy,
    TrendingUp,
} from "lucide-react";

const CATEGORIES = [
    { id: "football", name: "Kandanda" },
    { id: "basketball", name: "Mpira wa Kikapu" },
    { id: "athletics", name: "Riadha" },
    { id: "rugby", name: "Raga" },
    { id: "cricket", name: "Kriketi" },
    { id: "tennis", name: "Tenisi" },
    { id: "boxing", name: "Ngumi" },
];

const ArticlePage = () => {
    const { id } = useParams();
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [relatedArticles, setRelatedArticles] = useState([]);

    useEffect(() => {
        fetchArticle();
    }, [id]);

    const fetchArticle = async () => {
        try {
            const response = await axios.get(`${API}/content/${id}?channel=web`);
            setArticle(response.data);
            // Fetch related articles
            const relatedResponse = await axios.get(
                `${API}/content/public?category=${response.data.category}&limit=4`
            );
            setRelatedArticles(relatedResponse.data.filter(a => a.id !== id).slice(0, 3));
        } catch (error) {
            console.error("Error fetching article:", error);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString("sw-TZ", {
            day: "numeric",
            month: "long",
            year: "numeric"
        });
    };

    const totalViews = article ? 
        article.views_web + article.views_sms + article.views_whatsapp + article.views_ivr : 0;

    if (loading) {
        return (
            <div className="min-h-screen bg-obsidian flex items-center justify-center">
                <div className="animate-pulse text-electric text-xl font-heading">INAPAKIA...</div>
            </div>
        );
    }

    if (!article) {
        return (
            <div className="min-h-screen bg-obsidian flex flex-col items-center justify-center">
                <h1 className="text-2xl text-white font-heading mb-4">Makala Haipatikani</h1>
                <Link to="/" className="text-electric hover:underline">
                    Rudi Nyumbani
                </Link>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-obsidian" data-testid="article-page">
            {/* Navigation */}
            <nav className="nav-desktop">
                <div className="container-main">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/" className="flex items-center gap-2">
                            <Zap className="h-8 w-8 text-electric" />
                            <span className="dimbani-wordmark text-2xl text-white">
                                DIMBANI
                            </span>
                        </Link>
                        <Link 
                            to="/" 
                            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
                            data-testid="back-link"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Rudi Nyumbani
                        </Link>
                    </div>
                </div>
            </nav>

            {/* Hero Image */}
            <div className="relative h-[40vh] md:h-[50vh]" data-testid="article-hero">
                <img
                    src={upgradeImageUrl(article.image_url, 1600) || FALLBACK_HERO}
                    alt={article.title_sw}
                    loading="eager"
                    onError={(e) => { e.currentTarget.src = FALLBACK_HERO; }}
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/70 to-transparent" />
            </div>

            {/* Article Content */}
            <article className="container-main -mt-32 relative z-10 pb-16">
                <div className="max-w-3xl">
                    {/* Meta */}
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                        <Badge className="bg-electric text-white" data-testid="article-category">
                            {CATEGORIES.find(c => c.id === article.category)?.name || article.category}
                        </Badge>
                        <span className="text-white/50 text-sm flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(article.published_at || article.created_at)}
                        </span>
                        <span className="text-white/50 text-sm flex items-center gap-1">
                            <Eye className="h-3 w-3" />
                            {totalViews} wasomaji
                        </span>
                    </div>

                    {/* Title */}
                    <h1 
                        className="text-3xl md:text-5xl font-heading font-black text-white uppercase tracking-tight mb-6 text-glow"
                        data-testid="article-title"
                    >
                        {article.title_sw}
                    </h1>

                    {/* Author */}
                    <div className="flex items-center gap-3 mb-8 pb-8 border-b border-border/40">
                        <div className="w-10 h-10 rounded-full bg-electric flex items-center justify-center">
                            <span className="text-black font-bold text-lg">
                                {article.author_name?.charAt(0) || "D"}
                            </span>
                        </div>
                        <div>
                            <p className="text-white font-medium">{article.author_name || "Dimbani"}</p>
                            <p className="text-white/50 text-sm">Mwandishi</p>
                        </div>
                    </div>

                    {/* Summary */}
                    {article.summary_sw && (
                        <p className="text-xl text-white/80 leading-relaxed mb-8 font-medium" data-testid="article-summary">
                            {article.summary_sw}
                        </p>
                    )}

                    {/* Inline League Standings Widget */}
                    <InlineStandings competitionId={article.competition_id} />

                    {/* Content */}
                    <div 
                        className="prose prose-invert prose-lg max-w-none"
                        data-testid="article-content"
                    >
                        {article.content_sw.split('\n').map((paragraph, idx) => (
                            <p key={idx} className="text-white/70 leading-relaxed mb-4">
                                {paragraph}
                            </p>
                        ))}
                    </div>

                    {/* Tags */}
                    {article.tags && article.tags.length > 0 && (
                        <div className="mt-8 pt-8 border-t border-border/40">
                            <p className="text-white/50 text-sm mb-3">Lebo:</p>
                            <div className="flex flex-wrap gap-2">
                                {article.tags.map((tag, idx) => (
                                    <Badge 
                                        key={idx} 
                                        variant="outline" 
                                        className="border-border text-white/60"
                                    >
                                        #{tag}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Share & SMS CTA */}
                    <div className="mt-8 flex flex-col sm:flex-row gap-4">
                        <button 
                            className="btn-outline px-6 py-3 flex items-center justify-center gap-2"
                            onClick={() => {
                                navigator.share?.({
                                    title: article.title_sw,
                                    url: window.location.href
                                });
                            }}
                            data-testid="share-btn"
                        >
                            <Share2 className="h-4 w-4" />
                            Shiriki Makala
                        </button>
                        <div className="flex-1 p-4 bg-paper rounded-sm border border-border flex items-center gap-4">
                            <MessageCircle className="h-8 w-8 text-solar shrink-0" />
                            <div>
                                <p className="text-white text-sm font-medium">Pata habari kwa SMS</p>
                                <p className="text-white/50 text-xs">Tuma "HABARI" kwenda 22244</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Related Articles */}
                {relatedArticles.length > 0 && (
                    <div className="mt-16 pt-8 border-t border-border/40" data-testid="related-articles">
                        <h2 className="text-2xl font-heading font-bold text-white uppercase mb-6">
                            Habari Zinazofanana
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {relatedArticles.map((related, idx) => (
                                <Link
                                    key={related.id}
                                    to={`/makala/${related.id}`}
                                    className="card-base card-hover group"
                                    data-testid={`related-article-${idx}`}
                                >
                                    <div className="aspect-video relative overflow-hidden">
                                        <img
                                            src={upgradeImageUrl(related.image_url, 800) || FALLBACK_THUMB}
                                            alt={related.title_sw}
                                            loading="lazy"
                                            onError={(e) => { e.currentTarget.src = FALLBACK_THUMB; }}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                        />
                                    </div>
                                    <div className="p-4">
                                        <h3 className="font-heading font-bold text-white uppercase tracking-tight line-clamp-2 group-hover:text-electric transition-colors">
                                            {related.title_sw}
                                        </h3>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </article>

            {/* Footer */}
            <footer className="border-t border-border/40 bg-paper/50">
                <div className="container-main py-8">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-2">
                            <Zap className="h-6 w-6 text-electric" />
                            <span className="dimbani-wordmark text-xl text-white">DIMBANI</span>
                        </div>
                        <p className="text-white/40 text-sm">
                            &copy; {new Date().getFullYear()} Dimbani Sports
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default ArticlePage;

// --- Inline Standings Table (embedded inside articles) ---
const COMP_LEAGUE_MAP = {
    epl: "eng.1",
    laliga: "esp.1",
    serie_a: "ita.1",
    ucl: "uefa.champions",
    caf_cl: "caf.champions",
    nbc_premier: null, // Tanzania not in ESPN
    kpl: null, // Kenya not in ESPN
    null: "eng.1",
    "": "eng.1",
};

const InlineStandings = ({ competitionId }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const leagueId = COMP_LEAGUE_MAP[competitionId] ?? "eng.1";

    useEffect(() => {
        let alive = true;
        if (!leagueId) {
            setLoading(false);
            return;
        }
        axios
            .get(`${API}/live-standings/${leagueId}`)
            .then((r) => {
                if (alive) setData(r.data);
            })
            .catch(() => {})
            .finally(() => {
                if (alive) setLoading(false);
            });
        return () => {
            alive = false;
        };
    }, [leagueId]);

    if (!leagueId || loading) return null;
    if (!data || !data.entries?.length) return null;

    const top = data.entries.slice(0, 6);

    return (
        <Card className="bg-paper border-border mb-8 overflow-hidden" data-testid="inline-standings">
            <div className="bg-electric px-4 py-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Trophy className="h-4 w-4 text-white" />
                    <span className="text-white font-heading font-black text-xs uppercase tracking-widest">
                        Takwimu za Ligi · {data.league_name_sw || data.league_name}
                    </span>
                </div>
                <Link to="/msimamo" className="text-white/90 hover:text-white text-[10px] font-bold uppercase">
                    Angalia zote →
                </Link>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-subtle border-b border-border">
                        <tr className="text-left text-white/60 text-[10px] uppercase tracking-wider">
                            <th className="px-3 py-2 w-10 text-center">#</th>
                            <th className="px-3 py-2">Timu</th>
                            <th className="px-2 py-2 text-center w-10">MC</th>
                            <th className="px-2 py-2 text-center w-10">U</th>
                            <th className="px-2 py-2 text-center w-10 hidden sm:table-cell">S</th>
                            <th className="px-2 py-2 text-center w-10 hidden sm:table-cell">K</th>
                            <th className="px-2 py-2 text-center w-12 hidden md:table-cell">TF</th>
                            <th className="px-2 py-2 text-center w-12">Pt</th>
                        </tr>
                    </thead>
                    <tbody>
                        {top.map((e, idx) => (
                            <tr
                                key={e.team_id || idx}
                                className={`border-b border-border/30 ${idx < 4 ? "bg-electric/5" : ""}`}
                            >
                                <td className="px-3 py-2 text-center">
                                    <span
                                        className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                                            idx < 4
                                                ? "bg-electric text-white"
                                                : "bg-subtle text-white/70"
                                        }`}
                                    >
                                        {e.rank}
                                    </span>
                                </td>
                                <td className="px-3 py-2">
                                    <div className="flex items-center gap-2">
                                        {e.team_logo && (
                                            <img src={e.team_logo} alt="" className="w-5 h-5 object-contain" />
                                        )}
                                        <span className="text-white text-xs truncate max-w-[140px]">{e.team_short || e.team_name}</span>
                                    </div>
                                </td>
                                <td className="px-2 py-2 text-center text-white/70 font-mono text-xs">{e.played}</td>
                                <td className="px-2 py-2 text-center text-electric font-mono text-xs">{e.wins}</td>
                                <td className="px-2 py-2 text-center text-white/70 font-mono text-xs hidden sm:table-cell">{e.draws}</td>
                                <td className="px-2 py-2 text-center text-white/70 font-mono text-xs hidden sm:table-cell">{e.losses}</td>
                                <td className="px-2 py-2 text-center font-mono text-xs hidden md:table-cell">
                                    <span className={e.goal_difference > 0 ? "text-electric" : e.goal_difference < 0 ? "text-red-400" : "text-white/60"}>
                                        {e.goal_difference > 0 ? "+" : ""}{e.goal_difference}
                                    </span>
                                </td>
                                <td className="px-2 py-2 text-center text-white font-heading font-black text-sm">
                                    {e.points}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="px-4 py-1.5 text-[10px] text-white/40 flex items-center justify-between bg-subtle/40 border-t border-border">
                <span className="flex items-center gap-1">
                    <TrendingUp className="h-3 w-3 text-electric" />
                    Data ya moja kwa moja kutoka ESPN
                </span>
                <span>MC · U · S · K · TF · Pt</span>
            </div>
        </Card>
    );
};

